import type { FullRecord } from "@shared/types";

export type Tab = "dados" | "historico" | "docs";

export interface TempHistEntry { dt: string; txt: string; author_name: string | null; }
export interface TempDocEntry { nome: string; url: string; id?: number; }

export interface FormState {
  data: string;
  resp: string;
  cliente: string;
  ramo: string;
  status: string;
  corretor: string;
  prox_data: string;
  prox_mot: string;
  obs: string;
  motivo_perda: string;
  motivo_det: string;
  reativ_data: string;
}

export interface ModalState {
  form: FormState;
  tab: Tab;
  tempHist: TempHistEntry[];
  tempDocs: TempDocEntry[];
  conflict: boolean;
  saving: boolean;
}

export type Action =
  | { type: "setField"; field: keyof FormState; value: string }
  | { type: "setTab"; tab: Tab }
  | { type: "addTempHist"; entry: TempHistEntry }
  | { type: "delTempHist"; index: number }
  | { type: "addTempDoc"; doc: TempDocEntry }
  | { type: "delTempDoc"; index: number }
  | { type: "setConflict"; value: boolean }
  | { type: "setSaving"; value: boolean }
  | { type: "replaceFromServer"; record: FullRecord };

function isoFromDdMm(s: string | null): string {
  if (!s) return "";
  if (s.includes("-")) return s;
  const parts = s.split("/");
  if (parts.length < 2) return "";
  const d = parts[0]!.padStart(2, "0");
  const m = parts[1]!.padStart(2, "0");
  const y = parts[2] || String(new Date().getFullYear());
  return `${y.length === 2 ? "20" + y : y}-${m}-${d}`;
}

export function initialState(record: FullRecord | null): ModalState {
  if (!record) {
    return {
      form: { data: "", resp: "", cliente: "", ramo: "OBRA", status: "novo", corretor: "", prox_data: "", prox_mot: "", obs: "", motivo_perda: "", motivo_det: "", reativ_data: "" },
      tab: "dados",
      tempHist: [],
      tempDocs: [],
      conflict: false,
      saving: false,
    };
  }
  return {
    form: {
      data: isoFromDdMm(record.data),
      resp: record.resp || "",
      cliente: record.cliente,
      ramo: record.ramo || "OBRA",
      status: record.status,
      corretor: record.corretor || "",
      prox_data: record.prox_data || "",
      prox_mot: record.prox_mot || "",
      obs: record.obs || "",
      motivo_perda: record.motivo_perda || "",
      motivo_det: record.motivo_det || "",
      reativ_data: record.reativ_data || "",
    },
    tab: "dados",
    tempHist: record.historico.map(h => ({ dt: h.dt, txt: h.txt, author_name: h.author_name })),
    tempDocs: record.docs.map(d => ({ id: d.id, nome: d.nome, url: d.url })),
    conflict: false,
    saving: false,
  };
}

export function reducer(state: ModalState, action: Action): ModalState {
  switch (action.type) {
    case "setField":
      return { ...state, form: { ...state.form, [action.field]: action.value } };
    case "setTab":
      return { ...state, tab: action.tab };
    case "addTempHist":
      return { ...state, tempHist: [...state.tempHist, action.entry] };
    case "delTempHist":
      return { ...state, tempHist: state.tempHist.filter((_, i) => i !== action.index) };
    case "addTempDoc":
      return { ...state, tempDocs: [...state.tempDocs, action.doc] };
    case "delTempDoc":
      return { ...state, tempDocs: state.tempDocs.filter((_, i) => i !== action.index) };
    case "setConflict":
      return { ...state, conflict: action.value };
    case "setSaving":
      return { ...state, saving: action.value };
    case "replaceFromServer":
      return {
        ...state,
        tempHist: action.record.historico.map(h => ({ dt: h.dt, txt: h.txt, author_name: h.author_name })),
        tempDocs: action.record.docs.map(d => ({ id: d.id, nome: d.nome, url: d.url })),
      };
  }
}
